import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TAvailableLanguageCodes } from '@hubertai/screening-question-types';
import RuleConditionByInterviewProperty from '../RuleConditionByInterviewProperty';
import RuleConditionByRandomNumber from '../RuleConditionByRandomNumber';
import type { TRuleEditorCommonProps } from '../types';
import type { TSelectBase } from 'types/general.types';
import { EConditionType } from 'types/rules.types';
import type { TQuestionRuleCondition } from 'types/rules.types';
import HubertCombobox from 'components/HubertUI/HubertCombobox';

interface Props extends TRuleEditorCommonProps {
  condition: TQuestionRuleCondition;
  conditionTypeOptions: TSelectBase[];
  onTypeChange: (type: EConditionType) => void;
  onChange: (patch: Partial<TQuestionRuleCondition>) => void;
  renderQuestionAnswer?: () => ReactNode;
  // See RuleConditionByInterviewProperty's own allowedLanguages prop — omit to show every language.
  interviewPropertyLanguages?: TAvailableLanguageCodes[];
}

const RuleConditionShowIf = ({
  condition,
  isReadOnly,
  conditionTypeOptions,
  onTypeChange,
  onChange,
  renderQuestionAnswer,
  interviewPropertyLanguages,
}: Props) => {
  const { t } = useTranslation('Templates');

  return (
    <div>
      <div className="my-2 flex flex-row items-center gap-2">
        <div className="hubert-vertical-center">{t('question.ruleConditions.conditionByLabel')}</div>
        <HubertCombobox
          itemName="condition"
          value={condition.type}
          items={conditionTypeOptions}
          disabled={isReadOnly}
          onValueChange={(value) => {
            if (typeof value !== 'string') return;
            onTypeChange(value as EConditionType);
          }}
        />
      </div>

      {condition.type === EConditionType.QuestionAnswer ? (renderQuestionAnswer?.() ?? null) : null}

      {condition.type === EConditionType.InterviewProperty ? (
        <RuleConditionByInterviewProperty
          condition={condition}
          isReadOnly={isReadOnly}
          onChange={onChange}
          allowedLanguages={interviewPropertyLanguages}
        />
      ) : null}

      {condition.type === EConditionType.CandidateProperty ? <div className="candidate_property" /> : null}

      {condition.type === EConditionType.RandomNumber ? (
        <RuleConditionByRandomNumber condition={condition} isReadOnly={isReadOnly} onChange={onChange} />
      ) : null}
    </div>
  );
};

export default RuleConditionShowIf;
